import { useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { CharacterSheet, JournalEntry, TimelineEvent } from '@roll1/shared';
import type { TranslationKeys } from '../../i18n/types.js';
import { useI18n } from '../../i18n';
import { useSessionContext } from '../../contexts/SessionContext.js';
import { SearchPalette, type SearchAction, type SearchEntry } from '../search/SearchPalette';
import { buildSearchEntries, type SearchBeat, type SearchPage } from '../search/searchSources';
import { useRevealAsk } from '../search/useRevealAsk';
import { SrdArticleDialog, type SrdArticleTarget } from '../search/SrdArticleDialog';
import { useSrdIndex } from '../../lib/search/srdIndex';
import { requestOpen } from '../../lib/search/openRequest';
import { useStudioMonsterSpawn } from './StudioBestiaryConnected.js';
import type { StudioEntityRef } from './StudioRail.js';

/**
 * Ctrl+K of Подготовка — the app-wide search with the Studio's ways to open things: a beat opens its overview, an
 * entry / sheet / map opens in the main pane (or a tab, or the second pane — the palette's mode), a chronicle event
 * opens the table's letopis on it, an SRD thing opens its article. Row actions: «Открыть рядом», «В бит» (the beat
 * selected in the plan), «Показать игрокам…» (the existing confirmation), «На карту» (the working map).
 * Rendered inside the map panes provider (the working map is the panes').
 */
export interface StudioSearchProps {
  readonly open: boolean;
  readonly mode: 'open' | 'add' | 'beside';
  readonly onClose: () => void;
  readonly t: (key: keyof TranslationKeys) => string;
  readonly beats: readonly SearchBeat[];
  readonly entries: readonly JournalEntry[];
  readonly sheets: readonly CharacterSheet[];
  readonly pages: readonly SearchPage[];
  readonly timeline: readonly TimelineEvent[];
  readonly selectedBeatId: string | undefined;
  readonly currentBeatId: string | null;
  /** The thing open in the main pane now (a map there is «the open map»). */
  readonly focusRef: StudioEntityRef | undefined;
  readonly commands: readonly SearchEntry[];
  readonly onSelectBeat: (beatId: string) => void;
  readonly onPick: (ref: StudioEntityRef) => void;
  readonly onPin: (ref: StudioEntityRef) => void;
  readonly onAskShow: (pageId: string) => void;
  /** «В бит»: the selected beat takes the entry / sheet as material, a map as its map. Undefined — no beat selected. */
  readonly addToBeat: ((ref: StudioEntityRef) => void) | undefined;
  readonly selectedBeatName: string | undefined;
  readonly onExitStudio: () => void;
}

export function StudioSearch(props: StudioSearchProps): JSX.Element {
  const { t, open, mode, beats, entries: journal, sheets, pages, timeline, selectedBeatId, currentBeatId, focusRef, commands, selectedBeatName } = props;
  const { locale } = useI18n();
  const { mp, pages: pagesApi } = useSessionContext();
  const srd = useSrdIndex(open);
  const spawn = useStudioMonsterSpawn();
  const reveal = useRevealAsk(mp.send);
  const [article, setArticle] = useState<SrdArticleTarget | null>(null);
  // The handlers are read at the moment of a pick: the rows are rebuilt when the DATA changes, not on every render.
  const live = useRef({ props, spawn, reveal });
  live.current = { props, spawn, reveal };
  const canAddToBeat = props.addToBeat !== undefined;
  const playersPageId = pagesApi.activePageId;
  const participantId = mp.participant?.id ?? '';
  const participantName = mp.participant?.name ?? '';

  const entries = useMemo((): SearchEntry[] => {
    // Built only while open.
    if (!open) return [];
    const beside = mode === 'beside';
    const toBeatAction = (ref: StudioEntityRef): SearchAction[] =>
      !canAddToBeat || beside
        ? []
        : [{ id: 'to-beat', label: selectedBeatName === undefined ? t('search.act.toBeat') : `${t('search.act.toBeat')} «${selectedBeatName}»`, run: () => live.current.props.addToBeat?.(ref) }];
    const besideAction = (ref: StudioEntityRef): SearchAction[] =>
      beside ? [] : [{ id: 'beside', label: t('search.act.beside'), run: () => live.current.props.onPin(ref) }];
    const all = buildSearchEntries({
      t,
      locale,
      viewer: { isGM: true, participantId, name: participantName },
      entries: journal,
      pages,
      beats,
      sheets,
      timeline,
      srd,
      context: {
        beatIds: [currentBeatId, selectedBeatId].filter((id): id is string => typeof id === 'string'),
        openPageId: focusRef?.kind === 'page' ? focusRef.id : spawn.targetId ?? undefined,
        playersPageId,
      },
      open: {
        journal: (id) => live.current.props.onPick({ kind: 'journal', id }),
        page: (id) => live.current.props.onPick({ kind: 'page', id }),
        sheet: (id) => live.current.props.onPick({ kind: 'sheet', id }),
        beat: (id) => live.current.props.onSelectBeat(id),
        timeline: (id) => {
          live.current.props.onExitStudio();
          requestOpen({ kind: 'timeline', id });
        },
        srd: (kind, slug) => setArticle({ kind, slug }),
      },
      actionsFor: {
        journal: (e) => [
          ...besideAction({ kind: 'journal', id: e.id }),
          ...toBeatAction({ kind: 'journal', id: e.id }),
          // A GM-only entry: «Показать игрокам…» through the journal's reveal confirmation.
          ...(beside || e.visibility !== 'gm' ? [] : [{ id: 'reveal', label: t('search.act.reveal'), run: () => live.current.reveal.ask(e) }]),
        ],
        sheet: (sh) => [...besideAction({ kind: 'sheet', id: sh.id }), ...toBeatAction({ kind: 'sheet', id: sh.id })],
        page: (p) => [
          ...besideAction({ kind: 'page', id: p.id }),
          ...toBeatAction({ kind: 'page', id: p.id }),
          // The players' map is theirs already: no «Show» for it.
          ...(beside || p.id === playersPageId ? [] : [{ id: 'show', label: t('search.act.show'), run: () => live.current.props.onAskShow(p.id) }]),
        ],
        monster: (m) => [{
          id: 'to-map',
          label: spawn.targetName === undefined ? t('search.act.toMap') : `${t('search.act.toMap')} «${spawn.targetName}»`,
          run: () => live.current.spawn.spawn(m, 1),
          ...(spawn.targetId === null ? { disabledReason: t('search.srd.noMap') } : {}),
        }],
      },
      commands: beside ? [] : commands,
    });
    // «Open beside» chooses an object for the second pane: entries, sheets, maps only.
    return beside ? all.filter((e) => e.kind === 'journal' || e.kind === 'sheet' || e.kind === 'page') : all;
  }, [open, mode, t, locale, participantId, participantName, journal, pages, beats, sheets, timeline, srd, currentBeatId, selectedBeatId, focusRef, spawn.targetId, spawn.targetName, playersPageId, commands, canAddToBeat, selectedBeatName]);

  return (
    <>
      <SearchPalette
        open={open}
        entries={entries}
        t={t}
        onClose={props.onClose}
        placeholder={mode === 'beside' ? t('studio.full.besidePalette') : t('search.placeholder.gm')}
        scopeHint={t('search.emptyScope.gm')}
        loading={open && srd === null && mode !== 'beside'}
        testId="studio-palette"
      />
      {reveal.dialog}
      <SrdArticleDialog
        target={article}
        onClose={() => setArticle(null)}
        onSpawn={spawn.targetId === null ? undefined : (slug, withSheet, name) => {
          // The article's «token / + sheet»: on the working map, the same cell logic as «На карту».
          spawn.spawn({ slug, name: name ?? slug }, 1, withSheet);
        }}
      />
    </>
  );
}
